/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Action, State, fromTreeToList } from '@table-library/react-table-library/common';
import {
  Table,
  Header,
  HeaderRow,
  HeaderCell,
  Body,
  Row,
  Cell,
} from '@table-library/react-table-library/table';
import { CellTree, useTree } from '@table-library/react-table-library/tree';
import { CellSelect, Select, SelectClickTypes, SelectTypes as TreeSelectTypes, useRowSelect } from '@table-library/react-table-library/select';
import { DataPeriods } from '@utils/dataPeriods';
import { DATE, formatDateUTC } from '@utils/date';
import { SurveyType } from '@g17eco/types/surveyCommon';
import { Button } from 'reactstrap';
import { useAppDispatch, useAppSelector } from '../../../reducers';
import {
  CustomReportType,
  DateReportType,
  SURVEY_TYPE_LABELS,
  TableNodeWithSurveyData,
} from '../../../types/custom-report';
import { loadSurveyListSummaryByInitiativeId } from '../../../actions/survey';
import { useTheme } from '@table-library/react-table-library/theme';
import { useSurveyFilters } from '../hooks/useSurveyFilters';
import { Filters, SurveyFilters, getSurveyFilters } from '../util/filters';
import { BasicAlert } from '@g17eco/molecules/alert';
import { SelectFactory, SelectTypes } from '@g17eco/molecules/select/SelectFactory';
import { buildDateSurveyTree, StringIdTableNode, TableData } from '../util/treeNavigation';
import { SURVEY } from '@constants/terminology';
import { SurveySummary } from '../../../types/scorecard';

export interface DateSurveySelectionTableProps {
  initiativeId: string;
  defaultSelected?: string[];
  selectedSurveyIds: string[];
  handleSelectSurveys: (ids: string[]) => void;
}

const surveyOptions = [
  { label: SURVEY.CAPITALIZED_PLURAL, value: CustomReportType.Survey },
  { label: `Combined ${SURVEY.PLURAL}`, value: CustomReportType.SurveyAggregation },
];

const findNodesByIds = (tableData: TableData, ids?: string[], isSurveyId?: boolean): TableNodeWithSurveyData[] => {
  if (!ids || ids.length === 0) {
    return [];
  }

  const nodesMatch: TableNodeWithSurveyData[] = [];
  tableData.nodes.forEach((surveyType) =>
    surveyType.nodes?.forEach((period) => {
      period.nodes?.forEach((year) => {
        const nodesFound = year.nodes?.filter((survey) => {
          if (isSurveyId) {
            return ids.includes(survey._id);
          }
          return ids.includes(survey.id);
        }) as TableNodeWithSurveyData[];
        if (nodesFound && nodesFound.length > 0) {
          nodesMatch.push(...nodesFound);
        }
      });
    }),
  );
  return nodesMatch;
};

const getSurveyIds = (nodes: TableNodeWithSurveyData[]) => nodes.map((node) => node._id);
const getNodeIds = (surveys: SurveySummary[], ids: string[]) => {
  const matchSurveys = surveys.filter((item) => ids.includes(item._id));
  return matchSurveys.map((survey) => {
    const { type = SurveyType.Default, period = DataPeriods.Yearly } = survey;
    const year = formatDateUTC(survey.effectiveDate, DATE.YEAR_ONLY);
    const surveyType = SURVEY_TYPE_LABELS[type];
    return `${surveyType}-${period}-${year}-${survey._id}`;
  });
};

export const DateSurveySelectionTable = (props: DateSurveySelectionTableProps) => {
  const { initiativeId, defaultSelected = [], selectedSurveyIds = [], handleSelectSurveys } = props;
  const dispatch = useAppDispatch();
  const [selectedNodes, setSelectedNodes] = useState<TableNodeWithSurveyData[]>([]);
  const { filters, toggleFilter } = useSurveyFilters<DateReportType>({ surveyType: [CustomReportType.Survey] });
  const surveyListSummaryState = useAppSelector((s) => s.surveyListSummary);

  const surveyList: SurveySummary[] = useMemo(() => {
    return surveyListSummaryState.data || [];
  }, [surveyListSummaryState.data]);

  const { data: tableData, numOfNodes } = useMemo(
    () => buildDateSurveyTree(surveyList, filters),
    [surveyList, filters],
  );

  useEffect(() => {
    if (initiativeId) {
      dispatch(loadSurveyListSummaryByInitiativeId(initiativeId));
    }
  }, [dispatch, initiativeId]);

  useEffect(() => {
    // preset selected notes
    if (defaultSelected && defaultSelected.length > 0) {
      setSelectedNodes(findNodesByIds(tableData, defaultSelected, true));
    }
  }, [defaultSelected, tableData]);

  const yearOptions = useMemo(() => {
    const years = Array.from(
      new Set(surveyList.map((n) => formatDateUTC(n.effectiveDate, DATE.YEAR_ONLY))).values(),
    ).sort();
    return years.map((year) => ({ label: year, value: year }));
  }, [surveyList]);

  // This ids is for the purpose of opening the table tree
  const openTreeIds = useMemo(() => {
    let openIds: string[] = [];

    if (defaultSelected && defaultSelected.length > 0) {
      const nodeIds = getNodeIds(surveyList, defaultSelected);
      nodeIds.forEach((id) => {
        const parts = (id ?? '').split('-');
        // Must be 4 parts, `${surveyType}-${period}-${year}-${surveyId}`
        openIds =
          parts.length >= 4
            ? [...openIds, parts[0], `${parts[0]}-${parts[1]}`, `${parts[0]}-${parts[1]}-${parts[2]}`]
            : openIds;
      });
    }

    return openIds;
  }, [defaultSelected, surveyList]);

  const theme = useTheme({
    Table: `
      height: unset;
      max-height: 100%;
      --data-table-library_grid-template-columns: 70% 7% 7% minmax(0, 1fr);
    `,
  });

  const tree = useTree(
    tableData,
    { state: { ids: openTreeIds } },
    {
      treeIcon: {
        margin: '4px',
        iconDefault: <i className='fa mx-2' />,
        iconRight: <i className='fa fa-caret-right text-ThemeAccentExtradark mx-2' />,
        iconDown: <i className='fa fa-caret-down text-ThemeAccentExtradark mx-2' />,
      },
    },
  );

  const select = useRowSelect(
    tableData,
    {
      state: { ids: selectedNodes.map((item) => item.id) },
      onChange: onSelectChange,
    },
    {
      clickType: SelectClickTypes.RowClick,
      rowSelect: TreeSelectTypes.MultiSelect,
      buttonSelect: TreeSelectTypes.MultiSelect,
    },
  );

  function onSelectChange(_action: Action, state: State) {
    const nodes = findNodesByIds(tableData, state.ids);
    handleSelectSurveys(getSurveyIds(nodes));
  }

  const surveyFilters = getSurveyFilters({ surveyOptions, yearOptions });

  const handleToggleSelect = (select: Select<StringIdTableNode>) => {
    // get all visible ids on the screen
    const dataIds = fromTreeToList(tableData.nodes).map((item) => item.id);
    if (selectedSurveyIds.length < numOfNodes) {
      select.fns.onAddAll(dataIds);
      // expand the selected
      tree.fns.onAddAll(dataIds);
    } else {
      // only remove the visible ids on the screen
      select.fns.onRemoveByIds(dataIds);
    }
  };

  return (
    <div className='collapsible-table'>
      <div className='d-flex align-items-center'>
        <div className='mr-3 strong'>Filters:</div>
        {Object.values(SurveyFilters).map((filter: keyof Filters) => {
          const { label, options } = surveyFilters[filter];
          return (
            <SelectFactory
              selectType={SelectTypes.MultipleSelect}
              className='mr-2'
              key={filter}
              placeholder={label}
              options={options}
              values={filters[filter]}
              onChange={(op) => toggleFilter(filter, op)}
              isFlexibleSize
            />
          );
        })}
        <div className='ml-auto'>{`${selectedSurveyIds?.length ?? 0} ${SURVEY.PLURAL} selected`}</div>
      </div>
      <div
        className='mt-2'
        style={{
          height: '400px',
        }}
      >
        {tableData.nodes.length > 0 ? (
          <Table
            data={tableData}
            tree={tree}
            select={select}
            theme={theme}
            layout={{ fixedHeader: true, custom: true }}
          >
            {(tableList: StringIdTableNode[]) => (
              <>
                <Header>
                  <HeaderRow>
                    <HeaderCell>{SURVEY.CAPITALIZED_SINGULAR} dates</HeaderCell>
                    <HeaderCell>
                      <i className='fa fa-user-clock text-primary' />
                    </HeaderCell>
                    <HeaderCell>
                      <i className='fa fa-user-check text-success' />
                    </HeaderCell>
                    <HeaderCell>
                      <Button color='link' size='sm' onClick={() => handleToggleSelect(select)}>
                        {selectedSurveyIds.length >= numOfNodes ? 'Unselect all' : 'Select all'}
                      </Button>
                    </HeaderCell>
                  </HeaderRow>
                </Header>

                <Body>
                  {tableList.map((item) => (
                    <Row key={item.id} item={item}>
                      <CellTree item={item}>{item.name}</CellTree>
                      <Cell>{item?.status?.created ?? 0}</Cell>
                      <Cell>{item?.status?.verified ?? 0}</Cell>
                      {Array.isArray(item.nodes) ? <Cell /> : <CellSelect item={item} />}
                    </Row>
                  ))}
                </Body>
              </>
            )}
          </Table>
        ) : (
          <BasicAlert className='mt-3' type='warning'>
            No {SURVEY.PLURAL} are available
          </BasicAlert>
        )}
      </div>
    </div>
  );
};
